import { effect, inject, Injectable } from '@angular/core'
import { _filterNullishValues, getFetcher, topbar } from '@naturalcycles/js-lib'
import {
  AccountTM,
  BackendResponseFM,
  backendResponseReviver,
  ClientId,
  cookieService,
} from '@naturalcycles/shared'
import { MixpanelService } from '@src/app/core/services/analytics/mixpanel.service.js'
import { di } from '@src/app/core/services/di.service.js'
import { storageService } from '@src/app/core/services/storage.service.js'
import { SignalStore } from '@src/app/core/store/signalStore.js'
import { botReason, isCDP, isE2E } from '@src/app/core/util/e2e.util.js'
import { env } from '@src/environments/environment.js'
import { getUrlLang } from '../util/lang.util.js'
import { logger } from '../util/log.util.js'
import { sessionSigningService } from './sessionSigning.service.js'
import { userDeviceService } from './user-device.service.js'

const e2e = isE2E()
const tz = Intl.DateTimeFormat().resolvedOptions().timeZone

interface ApiHeaders {
  ts: number
  utcOffset: number
  tz: string
  sessionId?: string
  signature?: string
  clientId: ClientId.WebSignup
  deviceId?: string
  deviceUuid?: string
  distinctId?: string
  'X-User-Country'?: string
  'X-User-Region'?: string
  'X-Forced-Experiments': string
  uiLang: string
  e2e?: string
  br?: string
  cdp?: string

  /**
   * Meta click id and browser id
   * https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/fbp-and-fbc/
   */
  fbc?: string
  fbp?: string

  /**
   * Snapchat click id and cookie1
   * https://docs.snap.com/api/marketing-api/Conversions-API/UsingTheAPI#sending-click-id
   */
  scCid?: string
  uuid_c1?: string
}

export const api = getFetcher({
  // logResponse: true,
  baseUrl: env.apiUrl,
  credentials: 'include',
  timeoutSeconds: 70,
  jsonReviver: backendResponseReviver,
})
  .onBeforeRequest(async req => {
    const apiService = di.get(ApiService)
    const headers = await apiService.getExtraHeaders()
    // end todo
    Object.assign(req.init.headers, headers)

    if (!env.prod) {
      topbar.show()
    }
  })
  .onAfterResponse(res => {
    if (res.ok && res.body && res.req.responseType === 'json') {
      di.get(ApiService).onData(res.body)
    }

    if (!env.prod) {
      topbar.hide()
    }
  })

Object.assign(globalThis, { api: isE2E() ? api : undefined })
@Injectable({ providedIn: 'root' })
export class ApiService {
  private store = inject(SignalStore)
  private mixpanelService = inject(MixpanelService)

  constructor() {
    effect(() => {
      api.cfg.baseUrl = this.store.$overrideApiUrl() || env.apiUrl
    })
  }

  async getExtraHeaders(): Promise<ApiHeaders> {
    // We default to getting lang from the URL in case it's not set yet in the store
    const lang = this.store.$lang() || getUrlLang()
    const sessionId = this.store.$sessionId()
    const distinctId = this.mixpanelService.getDistinctId()
    const deviceId = this.mixpanelService.getDeviceId()

    const now = new Date()
    const ts = Math.round(now.getTime() / 1000)
    const utcOffset = -now.getTimezoneOffset()
    const deviceUuid = userDeviceService.deviceId()
    const e2eCountry = localStorage.getItem('e2eCountry') || undefined
    const e2eRegion = localStorage.getItem('e2eRegion') || undefined
    const fbc = cookieService.getCookie('_fbc')
    const fbp = cookieService.getCookie('_fbp')
    const scCid = new URLSearchParams(location.search).get('ScCid') || undefined
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const uuid_c1 = cookieService.getCookie('_scid')
    const clientId = ClientId.WebSignup

    const signature = sessionId ? await sessionSigningService.sign(sessionId, ts) : undefined

    return _filterNullishValues({
      ts,
      utcOffset,
      tz,
      sessionId,
      distinctId,
      deviceId,
      deviceUuid,
      'X-User-Country': e2eCountry,
      'X-User-Region': e2eRegion,
      'X-Forced-Experiments': this.getForcedExperimentsHeader(),
      uiLang: lang,
      e2e: compactBoolean(e2e),
      br: botReason ? String(botReason) : undefined,
      cdp: compactBoolean(isCDP),
      fbc,
      fbp,
      scCid,
      uuid_c1,
      signature,
      clientId,
    } satisfies ApiHeaders)
  }

  private getForcedExperimentsHeader(): string {
    const isAbbaTest = /(ABBA_)+\d*/
    const NO_AB = 'removeAllAbTest'

    return storageService
      .keys()
      .filter(key => isAbbaTest.test(key) || key === NO_AB)
      .map(key => {
        if (isAbbaTest.test(key)) return `${key.substring(5)}=${storageService.get(key)}`
        return `${key}=${storageService.get(key)}`
      })
      .join(',')
  }

  onData(data?: any): void {
    const r = data?.backendResponse as BackendResponseFM
    if (!r) return

    if (r.remoteConfigWeb) {
      this.store.$remoteConfig.set(r.remoteConfigWeb)
    }

    if (r.sessionId) {
      this.store.$sessionId.set(r.sessionId)
    }

    if (r.sessionInvalid) {
      logger.log(`sessionInvalid, removing from state`)
      this.store.$sessionId.set(undefined)
      this.store.$sessionEncryptionKeys.set(undefined)
      this.store.$account.set({} as AccountTM)
    } else if (r.account) {
      this.store.$account.set(r.account)
    }

    if (r.consentTerms) {
      this.store.$consent.set(r.consentTerms)
    }

    if (r.userLocale) {
      this.store.$userLocale.set(r.userLocale)
    }

    if (r.payment) {
      this.store.$payment.set(r.payment)
    }

    if (r.product) {
      this.store.$product.set(r.product)
    }

    if (r.cart) {
      this.store.$cart.set(r.cart)
    }

    if (r.order) {
      this.store.$order.set(r.order)
    }

    if (r.experiment) {
      this.store.$experiment.set(r.experiment)
    }

    if (r.subscriptions) {
      this.store.$subscriptions.set(r.subscriptions as any)
    }
  }
}

export function compactBoolean(v: any): '1' | undefined {
  return v ? '1' : undefined
}
